import React from 'react';
import { Link } from 'react-router-dom';
import {
  Headphones, Sparkles, Image as ImageIcon, Music, Rocket,
  ArrowRight, CheckCircle, Clock,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// ─── Translations ───────────────────────────────────
const COPY = {
  es: {
    overline: 'Servicios musicales',
    title: 'Un ecosistema completo',
    titleHighlight: 'para tu música',
    intro: 'Música pre-masterizada lista para lanzar, packs con descuento, portadas exclusivas y la guía de distribución privada del Ultimate Pack. Todo lo que necesitas para llevar tu proyecto al siguiente nivel, bajo un solo techo.',
    processOverline: 'Cómo funciona',
    processTitle: 'Un proceso',
    processAccent: 'simple y transparente',
    bottomTitle: '¿No sabes por dónde empezar?',
    bottomAccent: 'Hablamos.',
    bottomDesc: 'Cuéntanos tu proyecto y te recomendamos la mejor combinación de servicios. Respondemos en menos de 24 horas.',
    bottomPrimary: 'Ver Packs',
    bottomSecondary: 'Ver Catálogo',
    featured: 'Destacado',
    priceFrom: 'desde',
    priceUnit: 'por hora',
    priceOne: 'precio único',
    priceFree: 'GRATIS',
    priceIncluded: 'INCLUIDO',
    free: 'Gratis',
    included: 'Incluido',
    services: [
      { id: 'packs', name: 'Music Packs · Ahorra hasta 40%', price: 24.99, priceUnit: 'desde', highlight: true, ctaText: 'Ver Packs', ctaLink: '/packs',
        description: 'Compra paquetes de 5, 10 o 20 tracks listos para lanzar. Cuanto más grande el pack, mayor el descuento por canción.',
        features: ['Pack 5: $24.99 ($5.00 c/u)', 'Pack 10: $39.99 ($4.00 c/u) · MÁS POPULAR', 'Pack 20: $59.99 ($3.00 c/u)', 'WAV + MP3 de cada track', 'Portada exclusiva 3000×3000 por track', 'Licencia certificada incluida', 'Entrega 100% automática al pagar'] },
      { id: 'catalog', name: 'Catálogo · Tracks Individuales', price: 5.99, priceUnit: 'desde', highlight: false, ctaText: 'Explorar Catálogo', ctaLink: '/catalog',
        description: '97+ tracks pre-masterizados profesionalmente. Compra solo los que necesites con licencia comercial certificada.',
        features: ['Tracks ya masterizados nivel estudio', 'WAV + MP3 sin marcas', 'Portada OG exclusiva incluida', 'Licencia básica certificada', 'Descarga inmediata al pagar', 'Filtros por género, BPM y vibe'] },
      { id: 'playlist-promotion', name: 'OG Playlist Promotion', price: 19.99, priceUnit: 'desde', highlight: true, ctaText: 'Promocionar mi música', ctaLink: '/playlist-promotion',
        description: '¿Tu canción ya está en Spotify? Nosotros la incluimos en nuestra red de +100 playlists curadas a nivel mundial para que más oyentes la descubran. 3 planes según el alcance que necesites.',
        features: [
          'STARTER · $19.99 · 3 playlists OG · 30 días · Reporte de streams · Revisión 72h',
          'PRO · $45.99 · 5 playlists OG · 60 días · 1 post Instagram OG · Revisión 48h · MÁS POPULAR',
          'ELITE · $89.99 · 10 playlists OG · 90 días · IG + TikTok + Newsletter + Manager · Revisión 24h',
          'Garantía mínima de inclusión según plan',
          'Reporte mensual automático: popularidad + alcance',
          'Pago único · Sin suscripción · Renueva cuando quieras'
        ] },
      { id: 'custom-music', name: 'Música a Medida', price: 149, priceUnit: 'desde', highlight: false, ctaText: 'Ver Planes', ctaLink: '/membership',
        description: 'Canciones originales producidas específicamente para tu proyecto. Disponible mediante el Ultimate Pack o las membresías OG.',
        features: ['Brief detallado con nuestro equipo', 'Producción original exclusiva', 'Stems WAV + MP3 listos', 'Licencia exclusiva incluida', 'Hasta 2 revisiones', 'Entrega en 7-14 días'] },
      { id: 'ai-covers', name: 'Portadas OG (incluidas)', price: 0, priceUnit: 'INCLUIDO', highlight: false, ctaText: 'Ver Catálogo', ctaLink: '/catalog',
        description: 'Portada profesional y exclusiva al comprar cualquier track del catálogo. Generada con IA y diseñada al estilo del track.',
        features: ['Portada única generada por IA', 'Resolución 3000×3000 lista para Spotify/Apple', 'Diseño personalizado al estilo del track', 'Se descarga desde tu panel en 1 clic', 'Descarga ilimitada'] },
      { id: 'distribution', name: 'Guía de Distribución (exclusiva Ultimate)', price: 0, priceUnit: 'SOLO ULTIMATE PACK', highlight: false, ctaText: 'Comprar Ultimate Pack', ctaLink: '/ultimate',
        description: 'Acceso PRIVADO y exclusivo. La guía paso-a-paso para subir tu música a Spotify, Apple Music y más viene incluida ÚNICAMENTE con la compra del Ultimate Pack OG MUSIC.',
        features: ['Guía PDF paso-a-paso (privada · solo Ultimate Pack)', 'Recomendaciones de distribuidoras top del mercado', 'Checklist pre-lanzamiento', 'Estrategia de metadatos', 'Soporte por chat ante dudas', 'Disponible en tu Dashboard tras comprar el Ultimate Pack'] },
      { id: 'ultimate-pack', name: 'Ultimate Pack', price: 299, priceUnit: 'precio único', highlight: true, ctaText: 'Ver Ultimate Pack', ctaLink: '/ultimate',
        description: 'El paquete completo: 5 tracks personalizados + licencia exclusiva + guía de distribución privada.',
        features: ['5 canciones originales a medida', 'Licencia exclusiva en todos los tracks', 'WAV + MP3 + stems multipista', 'Portadas OG exclusivas incluidas', 'Guía de Distribución Exclusiva', 'Soporte prioritario 24/7'] },
    ],
    steps: [
      { n: 1, title: 'Música por track', desc: 'Compra cualquier track del catálogo y recibe WAV + MP3 + portada exclusiva + licencia básica al instante.' },
      { n: 2, title: 'Música ilimitada', desc: 'Elige una membresía OG y descarga toda la música que necesites con licencia comercial activa.' },
      { n: 3, title: 'Masteriza tu música', desc: 'Sube tu MP3, elige uno de los 8 presets OG y descarga tu WAV masterizado en minutos. 10 gratis al día.' },
      { n: 4, title: 'Recibe y lanza', desc: 'Todo lo que compres queda en tu Dashboard listo para descargar y subir a tu plataforma de distribución.' },
    ],
    trustBadges: [
      { t: 'Sin esperas', d: 'Compra inmediata · Mastering en minutos por ti mismo · Todo a un clic.' },
      { t: 'Calidad estudio', d: 'WAV 24-bit · -14 LUFS · Listo para Spotify y todas las plataformas.' },
      { t: 'Sin letra chica', d: 'Precios transparentes. Un pago, derechos de por vida en cada track.' },
    ],
  },
  en: {
    overline: 'Music services',
    title: 'A complete ecosystem',
    titleHighlight: 'for your music',
    intro: 'Pre-mastered ready-to-release music, discounted packs, exclusive covers and the private Ultimate Pack distribution guide. Everything you need to take your project to the next level, under one roof.',
    processOverline: 'How it works',
    processTitle: 'A',
    processAccent: 'simple, transparent process',
    bottomTitle: "Don't know where to start?",
    bottomAccent: "Let's talk.",
    bottomDesc: 'Tell us about your project and we will recommend the best combination of services. We reply in under 24 hours.',
    bottomPrimary: 'View Packs',
    bottomSecondary: 'View Catalog',
    featured: 'Featured',
    priceFrom: 'from',
    priceUnit: 'per hour',
    priceOne: 'one-time price',
    priceFree: 'FREE',
    priceIncluded: 'INCLUDED',
    free: 'Free',
    included: 'Included',
    services: [
      { id: 'packs', name: 'Music Packs · Save up to 40%', price: 24.99, priceUnit: 'from', highlight: true, ctaText: 'View Packs', ctaLink: '/packs',
        description: 'Buy bundles of 5, 10 or 20 ready-to-release tracks. The bigger the pack, the bigger the per-song discount.',
        features: ['Pack 5: $24.99 ($5.00 each)', 'Pack 10: $39.99 ($4.00 each) · MOST POPULAR', 'Pack 20: $59.99 ($3.00 each)', 'WAV + MP3 of each track', 'Exclusive 3000×3000 cover per track', 'Certified license included', '100% automated delivery on payment'] },
      { id: 'catalog', name: 'Catalog · Individual Tracks', price: 5.99, priceUnit: 'from', highlight: false, ctaText: 'Browse Catalog', ctaLink: '/catalog',
        description: '97+ professionally pre-mastered tracks. Buy only the ones you need with certified commercial license.',
        features: ['Already studio-mastered tracks', 'WAV + MP3 without watermarks', 'Exclusive OG cover included', 'Certified basic license', 'Instant download on payment', 'Filters by genre, BPM and vibe'] },
      { id: 'custom-music', name: 'Custom Music', price: 149, priceUnit: 'from', highlight: false, ctaText: 'View Plans', ctaLink: '/membership',
        description: 'Original songs produced specifically for your project. Available through the Ultimate Pack or OG memberships.',
        features: ['Detailed brief with our team', 'Exclusive original production', 'WAV + MP3 stems ready', 'Exclusive license included', 'Up to 2 revisions', 'Delivery in 7-14 days'] },
      { id: 'ai-covers', name: 'OG Covers (included)', price: 0, priceUnit: 'INCLUDED', highlight: false, ctaText: 'Browse Catalog', ctaLink: '/catalog',
        description: 'Professional exclusive AI-generated cover when you buy any track. Designed to match your track\'s style.',
        features: ['Unique AI-generated cover', '3000×3000 resolution for Spotify/Apple', 'Custom design matching your track', 'Download from your panel in 1 click', 'Unlimited download'] },
      { id: 'distribution', name: 'Distribution Guide (Ultimate exclusive)', price: 0, priceUnit: 'ULTIMATE PACK ONLY', highlight: false, ctaText: 'Buy Ultimate Pack', ctaLink: '/ultimate',
        description: 'PRIVATE & exclusive access. The step-by-step guide to upload your music to Spotify, Apple Music and more is included ONLY with the OG MUSIC Ultimate Pack purchase.',
        features: ['Step-by-step PDF guide (private · Ultimate Pack only)', 'Top distributor recommendations', 'Pre-launch checklist', 'Metadata strategy', 'Chat support', 'Available in your Dashboard after buying Ultimate Pack'] },
      { id: 'ultimate-pack', name: 'Ultimate Pack', price: 299, priceUnit: 'one-time price', highlight: true, ctaText: 'View Ultimate Pack', ctaLink: '/ultimate',
        description: 'The full package: 5 custom tracks + exclusive license + private distribution guide.',
        features: ['5 custom original songs', 'Exclusive license on all tracks', 'WAV + MP3 + multitrack stems', 'OG exclusive covers included', 'Exclusive Distribution Guide', 'Priority 24/7 support'] },
    ],
    steps: [
      { n: 1, title: 'Pick your tracks', desc: 'Browse the catalog and add the tracks you like to your cart, or grab a 5/10/20 Pack with bulk discount.' },
      { n: 2, title: 'Pay with PayPal or Crypto', desc: 'Secure checkout: PayPal (card without account included) or crypto (USDT/BTC). No Stripe.' },
      { n: 3, title: 'Automatic delivery', desc: 'Once payment is confirmed, you instantly receive WAV + MP3 + 3000×3000 cover + license PDF in your Dashboard and by email — zero human intervention.' },
      { n: 4, title: 'Distribute and release', desc: 'Your files are ready for Spotify, Apple Music and every platform. Upload-ready.' },
    ],
    trustBadges: [
      { t: 'Instant delivery', d: 'Buy → auto-delivered in seconds. No waiting, no queues, no reviews.' },
      { t: 'Studio quality', d: '24-bit WAV · -14 LUFS · Ready for Spotify and every platform.' },
      { t: 'No fine print', d: 'Transparent pricing. One payment, lifetime rights on every track.' },
    ],
  },
  pt: {
    overline: 'Serviços musicais',
    title: 'Um ecossistema completo',
    titleHighlight: 'para sua música',
    intro: 'Música pré-masterizada pronta para lançar, packs com desconto, capas exclusivas e o guia de distribuição privado do Ultimate Pack. Tudo que você precisa para levar seu projeto ao próximo nível, sob um mesmo teto.',
    processOverline: 'Como funciona',
    processTitle: 'Um processo',
    processAccent: 'simples e transparente',
    bottomTitle: 'Não sabe por onde começar?',
    bottomAccent: 'Vamos conversar.',
    bottomDesc: 'Conte sobre seu projeto e recomendamos a melhor combinação de serviços. Respondemos em menos de 24 horas.',
    bottomPrimary: 'Ver Packs',
    bottomSecondary: 'Ver Catálogo',
    featured: 'Destaque',
    priceFrom: 'a partir de',
    priceUnit: 'por hora',
    priceOne: 'preço único',
    priceFree: 'GRÁTIS',
    priceIncluded: 'INCLUÍDO',
    free: 'Grátis',
    included: 'Incluído',
    services: [
      { id: 'packs', name: 'Music Packs · Economize até 40%', price: 24.99, priceUnit: 'a partir de', highlight: true, ctaText: 'Ver Packs', ctaLink: '/packs',
        description: 'Compre pacotes de 5, 10 ou 20 tracks prontos para lançar. Quanto maior o pack, maior o desconto por música.',
        features: ['Pack 5: $24.99 ($5.00 cada)', 'Pack 10: $39.99 ($4.00 cada) · MAIS POPULAR', 'Pack 20: $59.99 ($3.00 cada)', 'WAV + MP3 de cada track', 'Capa exclusiva 3000×3000 por track', 'Licença certificada incluída', 'Entrega 100% automática no pagamento'] },
      { id: 'catalog', name: 'Catálogo · Tracks Individuais', price: 5.99, priceUnit: 'a partir de', highlight: false, ctaText: 'Explorar Catálogo', ctaLink: '/catalog',
        description: '97+ tracks pré-masterizados profissionalmente. Compre apenas os que precisar com licença comercial certificada.',
        features: ['Tracks já masterizados nível studio', 'WAV + MP3 sem marcas', 'Capa OG exclusiva incluída', 'Licença básica certificada', 'Download imediato no pagamento', 'Filtros por gênero, BPM e vibe'] },
      { id: 'playlist-promotion', name: 'OG Playlist Promotion', price: 19.99, priceUnit: 'a partir de', highlight: true, ctaText: 'Promover minha música', ctaLink: '/playlist-promotion',
        description: 'Sua música já está no Spotify? Nós a incluímos em nossa rede de +100 playlists curadas mundialmente para que mais ouvintes a descubram. 3 planos conforme o alcance que precisar.',
        features: [
          'STARTER · $19.99 · 3 playlists OG · 30 dias · Relatório de streams · Revisão 72h',
          'PRO · $45.99 · 5 playlists OG · 60 dias · 1 post Instagram OG · Revisão 48h · MAIS POPULAR',
          'ELITE · $89.99 · 10 playlists OG · 90 dias · IG + TikTok + Newsletter + Manager · Revisão 24h',
          'Garantia mínima de inclusão por plano',
          'Relatório mensal automático: popularidade + alcance',
          'Pagamento único · Sem assinatura · Renove quando quiser'
        ] },
      { id: 'custom-music', name: 'Música sob Medida', price: 149, priceUnit: 'a partir de', highlight: false, ctaText: 'Ver Planos', ctaLink: '/membership',
        description: 'Músicas originais produzidas para seu projeto. Disponível através do Ultimate Pack ou das memberships OG.',
        features: ['Brief detalhado com nosso time', 'Produção original exclusiva', 'Stems WAV + MP3 prontos', 'Licença exclusiva incluída', 'Até 2 revisões', 'Entrega em 7-14 dias'] },
      { id: 'ai-covers', name: 'Capas OG (incluídas)', price: 0, priceUnit: 'INCLUÍDO', highlight: false, ctaText: 'Ver Catálogo', ctaLink: '/catalog',
        description: 'Capa profissional exclusiva ao comprar qualquer track. Gerada por IA e desenhada no estilo do track.',
        features: ['Capa única gerada por IA', 'Resolução 3000×3000 para Spotify/Apple', 'Design personalizado ao estilo do track', 'Baixe do seu painel em 1 clique', 'Download ilimitado'] },
      { id: 'distribution', name: 'Guia de Distribuição (exclusivo Ultimate)', price: 0, priceUnit: 'SÓ ULTIMATE PACK', highlight: false, ctaText: 'Comprar Ultimate Pack', ctaLink: '/ultimate',
        description: 'Acesso PRIVADO e exclusivo. O guia passo-a-passo para subir sua música no Spotify, Apple Music e mais vem incluído APENAS na compra do Ultimate Pack OG MUSIC.',
        features: ['Guia PDF passo-a-passo (privado · só Ultimate Pack)', 'Recomendações de distribuidoras top do mercado', 'Checklist pré-lançamento', 'Estratégia de metadados', 'Suporte por chat', 'Disponível no seu Dashboard após comprar o Ultimate Pack'] },
      { id: 'ultimate-pack', name: 'Ultimate Pack', price: 299, priceUnit: 'preço único', highlight: true, ctaText: 'Ver Ultimate Pack', ctaLink: '/ultimate',
        description: 'O pacote completo: 5 tracks personalizados + licença exclusiva + guia de distribuição privado.',
        features: ['5 músicas originais sob medida', 'Licença exclusiva em todos os tracks', 'WAV + MP3 + stems multipistas', 'Capas OG exclusivas incluídas', 'Guia de Distribuição Exclusivo', 'Suporte prioritário 24/7'] },
    ],
    steps: [
      { n: 1, title: 'Escolha seus tracks', desc: 'Explore o catálogo e adicione os tracks que gostar ao carrinho, ou pegue um Pack 5/10/20 com desconto.' },
      { n: 2, title: 'Pague com PayPal ou Crypto', desc: 'Checkout seguro: PayPal (cartão sem conta incluído) ou crypto (USDT/BTC). Sem Stripe.' },
      { n: 3, title: 'Entrega automática', desc: 'Ao confirmar o pagamento, você recebe instantaneamente WAV + MP3 + capa 3000×3000 + licença PDF no Dashboard e por email — zero intervenção humana.' },
      { n: 4, title: 'Distribua e lance', desc: 'Seus arquivos prontos para Spotify, Apple Music e todas as plataformas. Pronto para subir.' },
    ],
    trustBadges: [
      { t: 'Entrega instantânea', d: 'Compra → entrega automática em segundos. Sem esperas, sem filas, sem revisões.' },
      { t: 'Qualidade estúdio', d: 'WAV 24-bit · -14 LUFS · Pronto para Spotify e todas as plataformas.' },
      { t: 'Sem letras miúdas', d: 'Preços transparentes. Um pagamento, direitos vitalícios em cada track.' },
    ],
  },
};

const SERVICE_ICONS = {
  'packs': Sparkles,
  'catalog': Music,
  'playlist-promotion': Rocket,
  'custom-music': Headphones,
  'ai-covers': ImageIcon,
  'distribution': Rocket,
  'ultimate-pack': Sparkles,
};

const TRUST_ICONS = [Clock, CheckCircle, Rocket];

export default function ServicesPage() {
  const { language } = useLanguage();
  const c = COPY[language] || COPY.es;

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="services-page">
      <div className="container-custom">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#E8720C] mb-4">{c.overline}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
            {c.title} <span className="gold-text">{c.titleHighlight}</span>
          </h1>
          <p className="text-lg text-white/60 leading-relaxed">{c.intro}</p>
        </div>

        {/* Services grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-24">
          {c.services.map((svc) => {
            const Icon = SERVICE_ICONS[svc.id] || Music;
            return (
              <div key={svc.id} className={`glass-card p-7 flex flex-col group hover:-translate-y-1 transition-transform ${svc.highlight ? 'border-[#E8720C]/30 ring-1 ring-[#E8720C]/10' : ''}`} data-testid={`service-card-${svc.id}`}>
                <div className="flex items-start justify-between mb-5">
                  <div className="w-12 h-12 rounded-xl bg-[#E8720C]/10 border border-[#E8720C]/20 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#E8720C]" />
                  </div>
                  {svc.highlight && (
                    <span className="px-2 py-1 text-[9px] font-bold uppercase tracking-[0.15em] bg-[#E8720C] text-white rounded-full">{c.featured}</span>
                  )}
                </div>
                <h3 className="text-xl font-bold mb-2">{svc.name}</h3>
                <p className="text-sm text-white/55 leading-relaxed mb-4">{svc.description}</p>
                <ul className="space-y-2 mb-6 flex-1">
                  {svc.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-white/70 leading-relaxed">
                      <CheckCircle className="w-3.5 h-3.5 text-[#E8720C] flex-shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex items-end justify-between pt-4 border-t border-white/5">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.15em] text-white/40 mb-1">{svc.priceUnit}</p>
                    {svc.price > 0 ? (
                      <p className="text-2xl font-bold gold-text">${svc.price}</p>
                    ) : (
                      <p className="text-lg font-bold text-green-400">{svc.priceUnit === c.priceFree ? c.free : c.included}</p>
                    )}
                  </div>
                  {svc.ctaLink && /^(mailto:|https?:|tel:)/.test(svc.ctaLink) ? (
                    <a href={svc.ctaLink} className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${svc.highlight ? 'bg-[#E8720C] hover:bg-[#C55A00] text-white' : 'bg-white/5 hover:bg-white/10 text-white border border-white/10'}`} data-testid={`service-cta-${svc.id}`}>
                      {svc.ctaText} <ArrowRight className="w-3 h-3" />
                    </a>
                  ) : (
                    <Link to={svc.ctaLink} className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${svc.highlight ? 'bg-[#E8720C] hover:bg-[#C55A00] text-white' : 'bg-white/5 hover:bg-white/10 text-white border border-white/10'}`} data-testid={`service-cta-${svc.id}`}>
                      {svc.ctaText} <ArrowRight className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Proceso */}
        <div className="glass-card p-10 mb-20">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-white/50 mb-3">{c.processOverline}</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              {c.processTitle} <span className="gold-text">{c.processAccent}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {c.steps.map((step) => (
              <div key={step.n} className="text-center">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#E8720C] to-[#C55A00] flex items-center justify-center mx-auto mb-4 text-white font-bold">{step.n}</div>
                <h3 className="font-bold text-sm mb-2">{step.title}</h3>
                <p className="text-xs text-white/50 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Trust badges */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
          {c.trustBadges.map((p, i) => {
            const Icon = TRUST_ICONS[i] || Clock;
            return (
              <div key={i} className="glass-card p-6 text-center">
                <Icon className="w-8 h-8 text-[#E8720C] mx-auto mb-3" />
                <h3 className="font-bold text-sm mb-1">{p.t}</h3>
                <p className="text-xs text-white/50">{p.d}</p>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="glass-card p-12 text-center bg-gradient-to-br from-[#E8720C]/10 to-transparent border-[#E8720C]/20">
          <h2 className="text-3xl md:text-4xl font-bold mb-3">
            {c.bottomTitle} <span className="gold-text">{c.bottomAccent}</span>
          </h2>
          <p className="text-white/60 mb-8 max-w-xl mx-auto">{c.bottomDesc}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/packs" className="inline-flex items-center gap-2 px-8 py-4 bg-[#E8720C] hover:bg-[#C55A00] rounded-full text-sm font-bold tracking-wide transition-all">{c.bottomPrimary} <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/catalog" className="inline-flex items-center gap-2 px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-sm font-bold tracking-wide transition-all">{c.bottomSecondary}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
